import { useState, useRef, useCallback, useEffect } from "react";
import { api } from "@/store";

/**
 * Hook for persistent column widths.
 * @param {string} context - e.g. "report:abc123" or "consolidated:xyz" or "template:t1"
 * @param {string} sheetName - current sheet name
 */
export function useColWidths(context, sheetName) {
  const [colWidths, setColWidths] = useState({});
  const resizingCol = useRef(null);
  const saveTimer = useRef(null);

  // Load saved widths when context/sheet changes
  useEffect(() => {
    if (!context || !sheetName) return;
    let cancelled = false;
    api.get("/user-preferences/col-widths", { params: { context, sheet_name: sheetName } })
      .then(res => { if (!cancelled) setColWidths(res.data.widths || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [context, sheetName]);

  // Debounced save to backend
  const persistWidths = useCallback((widths) => {
    if (!context || !sheetName) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put("/user-preferences/col-widths", {
        context, sheet_name: sheetName, widths,
      }).catch(() => {});
    }, 500);
  }, [context, sheetName]);

  const handleResizeStart = useCallback((colIdx, e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const th = e.target.parentElement;
    const startW = colWidths[String(colIdx)] || th.offsetWidth || 80;
    resizingCol.current = colIdx;

    const onMove = (me) => {
      const newW = Math.max(40, startW + (me.clientX - startX));
      setColWidths(prev => {
        const next = { ...prev, [String(colIdx)]: newW };
        persistWidths(next);
        return next;
      });
    };
    const onUp = () => {
      resizingCol.current = null;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [colWidths, persistWidths]);

  const getColStyle = useCallback((colIdx) => {
    const w = colWidths[String(colIdx)];
    return w
      ? { width: w, minWidth: w, maxWidth: w, overflow: "hidden" }
      : { width: 100, minWidth: 40, overflow: "hidden" };
  }, [colWidths]);

  return { colWidths, handleResizeStart, getColStyle };
}
